// W35 / M35 — Riportok.
//
// A tervben beágyazott (külső) riport szerepel, de amíg nincs adatkapcsolat, a
// saját adatokból számolt nézet látszik — ez a fájl ezt a nézetet építi fel.
//
// Nincs diagramkönyvtár: a sávokat a ds.css `.prog` osztálya rajzolja, a
// szélességet százalékban adjuk meg. Így a sötét témában és nyomtatásban is
// helyes marad, és nem hoz be egy 300 kB-os függőséget egyetlen oszlopdiagramért.
//
// A számolás és a CSV-készítés szándékosan tiszta függvény (`buildReport`,
// `buildCsv`): DOM nélkül, adatbázis nélkül tesztelhető.

import { t } from '../lib/i18n';
import { emptyState } from '../lib/components';
import { h, icon, icons } from '../lib/dom';
import type { PersonView, ProjectView } from '../lib/data';
import { formatDeadline, formatElapsed, type TaskView } from '../lib/model';

// ---------------------------------------------------------------------------
// Számolás
// ---------------------------------------------------------------------------

/// Lezárt státuszok: ezekre már nem vár munka.
const closedStatuses: ReadonlySet<string> = new Set(['completed', 'failed', 'unaccepted']);

export type ProjectReportRow = {
  id: string;
  title: string;
  taskCount: number;
  completedCount: number;
  /// 0..1; feladat nélküli projektnél 0.
  completionRatio: number;
  overdueCount: number;
  trackedSeconds: number;
};

export type PersonReportRow = {
  id: string;
  fullName: string;
  /// Rá osztott, még nyitott feladatok.
  openCount: number;
  completedCount: number;
  overdueCount: number;
  trackedSeconds: number;
};

export type ReportSummary = {
  projects: ProjectReportRow[];
  people: PersonReportRow[];
  taskCount: number;
  completedCount: number;
  openCount: number;
  overdueCount: number;
  completionRatio: number;
  trackedSeconds: number;
  /// Határidőre teljesített / határidővel lezárt feladatok aránya. `undefined`,
  /// ha egyetlen lezárt feladatnak sincs határideje — ilyenkor nincs mit mérni.
  onTimeRatio?: number;
  /// Átlagos átfutás kiosztástól lezárásig, napban. `undefined`, ha egyetlen
  /// feladatnál sincs meg mindkét időpont.
  averageLeadTimeDays?: number;
  /// Elutasított feladatok aránya az elbírált (elfogadott vagy elutasított)
  /// feladatokhoz mérve.
  declineRatio?: number;
};

/// Összesítés a betöltött projektekből és emberekből.
///
/// A mért idő a feladatok `elapsedSeconds` értékéből jön (a kézi felülírást a
/// model.ts már figyelembe vette). A terhelést azért a NYITOTT feladatokkal
/// mérjük, mert a lezárt munka már nem foglalja az illetőt.
export function buildReport(projects: ProjectView[], people: PersonView[]): ReportSummary {
  const projectRows: ProjectReportRow[] = projects.map((project) => {
    const completed = project.tasks.filter((t) => t.status === 'completed').length;
    return {
      id: project.id,
      title: project.title,
      taskCount: project.tasks.length,
      completedCount: completed,
      completionRatio: project.tasks.length === 0 ? 0 : completed / project.tasks.length,
      overdueCount: project.tasks.filter((t) => t.isOverdue).length,
      trackedSeconds: project.tasks.reduce((sum, t) => sum + t.elapsedSeconds, 0),
    };
  });

  const allTasks = projects.flatMap((p) => p.tasks);

  const personRows: PersonReportRow[] = people.map((person) => {
    const mine = allTasks.filter((t) => t.assigneeIds.includes(person.id));
    return {
      id: person.id,
      fullName: person.fullName,
      openCount: mine.filter((t) => !closedStatuses.has(t.status)).length,
      completedCount: mine.filter((t) => t.status === 'completed').length,
      overdueCount: mine.filter((t) => t.isOverdue).length,
      trackedSeconds: mine.reduce((sum, t) => sum + t.elapsedSeconds, 0),
    };
  });

  // Hatarido-tartas: csak azok a lezart feladatok szamitanak, amelyeknek VOLT
  // hataridejuk es van lezarasi idopontjuk. Kulonben nincs mihez merni.
  const closedWithDeadline = allTasks.filter(
    (task) => task.finishedAt !== undefined && task.deadline !== undefined,
  );
  const onTime = closedWithDeadline.filter(
    (task) => task.finishedAt!.getTime() <= task.deadline!.getTime(),
  );

  // Atfutas: kiosztastol lezarasig.
  const withLeadTime = allTasks.filter(
    (task) => task.finishedAt !== undefined && task.createdAt !== undefined,
  );
  const leadDaysTotal = withLeadTime.reduce(
    (sum, task) =>
      sum + (task.finishedAt!.getTime() - task.createdAt!.getTime()) / 86400000,
    0,
  );

  // Elutasitasi arany: az ELBIRALT feladatokhoz merve. Ami meg elfogadasra
  // var, az se nem elfogadott, se nem elutasitott — nem szamit bele.
  const declined = allTasks.filter((task) => task.status === 'unaccepted').length;
  const judged = allTasks.filter(
    (task) => task.status !== 'waitingToAccept' && task.status !== 'todo',
  ).length;

  const completed = allTasks.filter((t) => t.status === 'completed').length;


  return {
    projects: projectRows,
    people: personRows,
    taskCount: allTasks.length,
    completedCount: completed,
    openCount: allTasks.filter((t) => !closedStatuses.has(t.status)).length,
    overdueCount: allTasks.filter((t) => t.isOverdue).length,
    completionRatio: allTasks.length === 0 ? 0 : completed / allTasks.length,
    onTimeRatio:
      closedWithDeadline.length === 0 ? undefined : onTime.length / closedWithDeadline.length,
    averageLeadTimeDays:
      withLeadTime.length === 0 ? undefined : leadDaysTotal / withLeadTime.length,
    declineRatio: judged === 0 ? undefined : declined / judged,
    trackedSeconds: allTasks.reduce((sum, t) => sum + t.elapsedSeconds, 0),
  };
}

export function percent(ratio: number): number {
  return Math.round(ratio * 100);
}

/// Órára kerekített mért idő, magyar tizedesvesszővel (Excel így fogadja el).
export function hours(seconds: number): string {
  return (seconds / 3600).toFixed(2).replace('.', ',');
}

/// CSV-cella: pontosvesszőt, idézőjelet és sortörést tartalmazó értéket idézünk.
function csvCell(value: string | number): string {
  const text = String(value);
  return /[";\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/// A CSV fejlecenek KULCSAI. Kesz szoveget nem tarolhat modul szintu tabla:
/// a szotar csak a render elott erkezik meg.
const csvHeaderKeys = [
  'report.csvSection',
  'report.csvName',
  'report.csvTask',
  'report.csvDone',
  'report.csvOpen',
  'report.csvOverdue',
  'report.csvCompletion',
  'report.csvTracked',
];

/// A riport CSV-je.
///
/// Pontosvessző az elválasztó és vessző a tizedesjel — a magyar Excel ezt
/// nyitja meg oszlophelyesen. Egy tábla, `Szakasz` oszloppal: először az
/// összesítés, majd a projektek, végül az emberek.
export function buildCsv(projects: ProjectView[], people: PersonView[]): string {
  const report = buildReport(projects, people);

  const lines: string[][] = [csvHeaderKeys.map((key) => t(key))];

  lines.push([
    t('report.csvTotal'),
    t('report.csvAllProjects'),
    String(report.taskCount),
    String(report.completedCount),
    String(report.openCount),
    String(report.overdueCount),
    String(percent(report.completionRatio)),
    hours(report.trackedSeconds),
  ]);

  for (const row of report.projects) {
    lines.push([
      t('report.csvProject'),
      row.title,
      String(row.taskCount),
      String(row.completedCount),
      String(row.taskCount - row.completedCount),
      String(row.overdueCount),
      String(percent(row.completionRatio)),
      hours(row.trackedSeconds),
    ]);
  }

  for (const row of report.people) {
    lines.push([
      'Ember',
      row.fullName,
      String(row.openCount + row.completedCount),
      String(row.completedCount),
      String(row.openCount),
      String(row.overdueCount),
      '',
      hours(row.trackedSeconds),
    ]);
  }

  // CRLF: a Windows-os táblázatkezelők ezt várják.
  return lines.map((cells) => cells.map(csvCell).join(';')).join('\r\n');
}

// ---------------------------------------------------------------------------
// Nézet
// ---------------------------------------------------------------------------

export type ReportsPageOptions = {
  projects: ProjectView[];
  people: PersonView[];
  /// Az összeállított CSV átadása a letöltésnek. A fájlba írás nem ide tartozik.
  onExport: (csv: string) => void;
};

function statCard(options: {
  label: string;
  value: string;
  hint: string;
  tone?: string;
}): HTMLElement {
  return h(
    'div',
    { class: 'card pad16 col', style: { gap: '4px' } },
    h('span', { class: 'label', text: options.label }),
    h('span', {
      class: 'mono w7',
      style: { fontSize: '30px', lineHeight: '1.1', color: options.tone ?? 'var(--tp)' },
      text: options.value,
    }),
    h('span', { class: 'caption', text: options.hint }),
  );
}

/// Vízszintes sáv a `.prog` osztállyal — a diagram maga a haladásjelző.
function bar(options: { ratio: number; label: string }): HTMLElement {
  const width = Math.round(Math.max(0, Math.min(1, options.ratio)) * 100);
  return h(
    'div',
    {
      class: 'prog',
      role: 'img',
      'aria-label': options.label,
    },
    h('i', { style: { width: `${width}%` } }),
  );
}

/// Egy sor a sávdiagramban: név · sáv · érték.
function barRow(options: {
  name: string;
  ratio: number;
  value: string;
  ariaLabel: string;
  lead?: HTMLElement;
}): HTMLElement {
  return h(
    'div',
    { class: 'row g12' },
    options.lead ?? null,
    h('span', {
      class: 'body-sm',
      style: { width: '190px', flex: 'none', minWidth: '0' },
      text: options.name,
    }),
    h('span', { class: 'grow', style: { minWidth: '0' } }, bar({ ratio: options.ratio, label: options.ariaLabel })),
    h('span', {
      class: 'mono caption',
      style: { width: '86px', flex: 'none', textAlign: 'right' },
      text: options.value,
    }),
  );
}


function overdueRow(task: TaskView, projectTitle: string, people: PersonView[]): HTMLElement {
  const names = task.assigneeIds
    .map((id) => people.find((p) => p.id === id)?.fullName)
    .filter((name): name is string => name !== undefined);

  return h(
    'div',
    { class: 'row g12', style: { padding: '8px 0', borderTop: '1px solid var(--border)' } },
    h('span', { class: 'body-sm grow', style: { minWidth: '0' }, text: task.title }),
    h('span', {
      class: 'caption',
      style: { width: '180px', flex: 'none' },
      text: projectTitle,
    }),
    h('span', {
      class: 'caption',
      style: { width: '150px', flex: 'none' },
      text: names.length > 0 ? names.join(', ') : t('report.noAssignee'),
    }),
    h('span', {
      class: 'body-sm overdue',
      style: { width: '160px', flex: 'none' },
      text: task.deadline ? formatDeadline(task.deadline) : '—',
    }),
  );
}

/// A terv bal oldali „Mentett riportok" listája. Ezek NEM mentett, szerver
/// oldali riportok — a webnek nincs riport-tárolója —, hanem a saját
/// adatokból számolt nézetek. A lista ezt ki is írja.
type ReportViewKey = 'load' | 'onTime' | 'time' | 'overdue';

const reportViews: { key: ReportViewKey; titleKey: string; captionKey: string }[] = [
  { key: 'load', titleKey: 'report.viewLoadTitle', captionKey: 'report.viewLoadCaption' },
  { key: 'onTime', titleKey: 'report.viewOnTimeTitle', captionKey: 'report.viewOnTimeCaption' },
  { key: 'time', titleKey: 'report.viewTimeTitle', captionKey: 'report.viewTimeCaption' },
  {
    key: 'overdue',
    titleKey: 'report.viewOverdueTitle',
    captionKey: 'report.viewOverdueCaption',
  },
];

/// Függőleges oszlopdiagram a terv szerint: oszlopok, alattuk a monogram.
///
/// Diagramkönyvtár nélkül, mert egyetlen diagramért nem éri meg 100 kB-ot
/// szállítani — a sávokat a böngésző rajzolja.
function barChart(
  rows: { label: string; value: number; title: string }[],
): HTMLElement {
  const max = Math.max(1, ...rows.map((r) => r.value));

  return h(
    'div',
    { class: 'col g12', style: { minWidth: '0', overflowX: 'auto' } },
    h(
      'div',
      { class: 'row', style: { alignItems: 'flex-end', gap: '28px', height: '150px' } },
      ...rows.map((row) =>
        h('div', {
          title: `${row.title}: ${row.value}`,
          'aria-label': `${row.title}: ${row.value}`,
          style: {
            width: '26px',
            flex: 'none',
            height: `${Math.round((row.value / max) * 150)}px`,
            minHeight: row.value > 0 ? '4px' : '0',
            borderRadius: '4px 4px 0 0',
            background: 'var(--accent)',
            opacity: String(0.45 + 0.55 * (row.value / max)),
          },
        }),
      ),
    ),
    h(
      'div',
      { class: 'row', style: { gap: '28px' } },
      ...rows.map((row) =>
        h('span', {
          class: 'caption',
          style: { width: '26px', flex: 'none', textAlign: 'center' },
          title: row.title,
          text: row.label,
        }),
      ),
    ),
  );
}

/// Monogram a diagram tengelyére.
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function reportsPage(options: ReportsPageOptions): HTMLElement {
  const { projects, people } = options;
  const report = buildReport(projects, people);

  let view: ReportViewKey = 'load';
  const panelHost = h('div', {
    class: 'panel pad20 col g14',
    style: { flexGrow: '1', minWidth: '0', gap: '14px' },
  });
  const libraryHost = h('div', { class: 'panel', style: { overflow: 'hidden' } });

  const header = h(
    'header',
    {
      class: 'row between g12 wrap',
      style: { padding: '12px 20px', borderBottom: '1px solid var(--border)' },
    },
    h(
      'div',
      { class: 'col', style: { gap: '2px', minWidth: '0' } },
      h('h2', { class: 'h3', text: t('report.title') }),
      h('span', {
        class: 'caption',
        text: t('report.ownDataHint'),
      }),
    ),
    h('button', {
      class: 'btn btn-primary btn-sm',
      type: 'button',
      text: t('report.exportCsvButton'),
      onClick: () => options.onExport(buildCsv(projects, people)),
    }),
  );

  if (projects.length === 0) {
    return h(
      'div',
      { class: 'col', style: { gap: '0' } },
      header,
      emptyState({
        title: 'Nincs mit kimutatni',
        text: t('report.emptyText'),
      }),
    );
  }

  // A savok egymashoz merve: a leghosszabb projekt a 100%.
  const maxSeconds = Math.max(1, ...report.projects.map((p) => p.trackedSeconds));

  const byTime = [...report.projects].sort((a, b) => b.trackedSeconds - a.trackedSeconds);
  const byLoad = [...report.people]
    .filter((p) => p.openCount > 0 || p.trackedSeconds > 0)
    .sort((a, b) => b.openCount - a.openCount || b.trackedSeconds - a.trackedSeconds);

  const overdue = projects
    .flatMap((project) => project.tasks.filter((t) => t.isOverdue).map((task) => ({ project, task })))
    .sort((a, b) => (a.task.deadline?.getTime() ?? 0) - (b.task.deadline?.getTime() ?? 0));

  function renderLibrary(): void {
    libraryHost.replaceChildren(
      ...reportViews.map((item) =>
        h(
          'button',
          {
            type: 'button',
            class: `navitem${item.key === view ? ' on' : ''}`,
            style: {
              borderRadius: '0',
              height: '52px',
              width: '100%',
              border: '0',
              font: 'inherit',
              cursor: 'pointer',
              textAlign: 'left',
            },
            onClick: () => {
              view = item.key;
              render();
            },
          },
          h('span', { class: 'ic' }, icon(icons.chart, 18)),
          h(
            'span',
            { class: 'col', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
            h('span', { class: 'body-sm w6', text: t(item.titleKey) }),
            h('span', { class: 'caption', text: t(item.captionKey) }),
          ),
        ),
      ),
    );
  }

  function renderPanel(): void {
    const current = reportViews.find((v) => v.key === view)!;

    const content: (HTMLElement | null)[] = [
      h(
        'div',
        { class: 'col', style: { gap: '2px' } },
        h('span', {
          class: 'h3',
          text: `${t(current.titleKey)} — ${t(current.captionKey).toLowerCase()}`,
        }),
        h('span', {
          class: 'caption',
          text: t('report.ownDataShort'),
        }),
      ),
    ];

    if (view === 'load') {
      content.push(
        byLoad.length === 0
          ? h('span', { class: 'body-sm ts', text: 'Senkinek nincs nyitott feladata.' })
          : barChart(
              byLoad.map((row) => ({
                label: initials(row.fullName),
                title: row.fullName,
                value: row.openCount,
              })),
            ),
      );
    } else if (view === 'onTime') {
      content.push(
        ...report.projects.map((row) =>
          barRow({
            name: row.title,
            ratio: row.completionRatio,
            value: `${percent(row.completionRatio)}% · ${row.completedCount}/${row.taskCount}`,
            ariaLabel: t('report.barCompletion', [row.title, String(percent(row.completionRatio))]),
          }),
        ),
      );
    } else if (view === 'time') {
      content.push(
        ...byTime.map((row) =>
          barRow({
            name: row.title,
            ratio: row.trackedSeconds / maxSeconds,
            value: formatElapsed(row.trackedSeconds),
            ariaLabel: t('report.barTracked', [row.title, formatElapsed(row.trackedSeconds)]),
          }),
        ),
      );
    } else {
      content.push(
        h('span', {
          class: 'caption',
          text:
            overdue.length > 0
              ? t('report.overdueSlipped', [String(overdue.length)])
              : t('report.noOverdue'),
        }),
        ...overdue.slice(0, 20).map(({ project, task }) => overdueRow(task, project.title, people)),
        overdue.length > 20
          ? h('span', { class: 'caption', text: t('report.andMoreTasks', [String(overdue.length - 20)]) })
          : null,
      );
    }

    panelHost.replaceChildren(...content.filter((c): c is HTMLElement => c !== null));
  }

  function render(): void {
    renderLibrary();
    renderPanel();
  }
  render();

  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    header,
    h(
      'div',
      {
        class: 'row',
        style: { gap: '20px', padding: '20px', alignItems: 'flex-start', flexWrap: 'wrap' },
      },
      h(
        'div',
        { class: 'col g10', style: { width: '300px', flex: 'none', gap: '10px' } },
        h('span', { class: 'label', text: t('report.views') }),
        libraryHost,
        // A terv „Nincs beallitva az adatkapcsolat" kartyat mutat. Ez IGAZ:
        // kulso riportszolgaltatas nincs bekotve, es nem is tervezett — ezert
        // a kartya nem „Beallitas" gombot kinal, hanem megmondja, mi latszik.
        h(
          'div',
          { class: 'card pad16 col', style: { gap: '8px' } },
          h(
            'span',
            {
              class: 'ic',
              style: {
                width: '30px',
                height: '30px',
                borderRadius: '8px',
                background: 'var(--info-soft)',
                color: 'var(--info)',
              },
            },
            icon(icons.info, 16),
          ),
          h('span', { class: 'body-sm w6', text: t('report.ownDataTitle') }),
          h('span', {
            class: 'caption',
            text:
              t('report.ownDataBody'),
          }),
        ),
      ),
      h(
        'div',
        { class: 'col g16', style: { flex: '1 1 0', minWidth: '320px', gap: '16px' } },
        h(
          'div',
          { class: 'grid c4' },
          statCard({
            label: t('report.onTimeLabel'),
            value:
              report.onTimeRatio === undefined ? '—' : `${percent(report.onTimeRatio)}%`,
            hint:
              report.onTimeRatio === undefined
                ? t('report.onTimeNoData')
                : t('report.onTimeSource'),
          }),
          statCard({
            label: t('report.leadTimeLabel'),
            value:
              report.averageLeadTimeDays === undefined
                ? '—'
                : t('report.leadTimeDays', [
                    report.averageLeadTimeDays.toFixed(1).replace('.', ','),
                  ]),
            hint: t('report.leadTimeSpan'),
          }),
          statCard({
            label: t('report.declineLabel'),
            value:
              report.declineRatio === undefined ? '—' : `${percent(report.declineRatio)}%`,
            hint: t('report.declineHint'),
            tone:
              report.declineRatio !== undefined && report.declineRatio > 0.1
                ? 'var(--warn)'
                : undefined,
          }),
          statCard({
            label: t('report.trackedLabel'),
            value: hours(report.trackedSeconds),
            hint: t('report.trackedHint'),
          }),
        ),
        panelHost,
      ),
    ),
  );
}
